/**
 * SCRUM-1032 QC round 4, item 3 — turning the Purpose cell back into the id
 * the API wants, without ever clearing a purpose by accident.
 *
 * The cell holds a name, because that is what the dropdown offers and what a
 * fill-down copies. The write takes `decoderType`, an id. Resolving the one to
 * the other used to end in `null` whenever the name was not found in the
 * decoder-type list — and `null` is not "leave it alone" to this API, it is
 * "clear it". The row's purpose came back empty and red, which is what Wei Woon
 * filmed mid-save on 27 Sep: the five rows the save had already reached had
 * lost the purpose they came with.
 *
 * So the three cases are told apart:
 *
 * - the cell is empty      → `{ id: null }`, a clear the user asked for
 * - the name resolves      → `{ id }`
 * - the name does not      → `{ unresolved: name }`, and the caller must not
 *                            write the row at all
 *
 * An unresolved name is not a user error to be swallowed. It means the list of
 * types this screen holds does not contain what the row is showing — a type
 * beyond the page it asked for, a name the server spells differently, a row
 * from a sub-controller with its own types. Writing null there destroys data
 * the user can see on screen; refusing the row keeps it, and says so.
 */

export interface DecoderTypeOption {
  id?: number | string
  name?: string
}

export type DecoderPurposeId =
  /** Write this id — including `null`, which clears a purpose deliberately. */
  | { id: number | null }
  /** Do not write this row: its purpose is not one we can name an id for. */
  | { unresolved: string }

/** Loose compare: the server's names vary in case and in spacing. */
const normalise = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')

const positiveId = (value: unknown): number | null => {
  const id = Number(value)
  return Number.isFinite(id) && id > 0 ? id : null
}

export const resolveDecoderPurposeId = (
  row: any,
  types: DecoderTypeOption[] | undefined,
): DecoderPurposeId => {
  const name = String(row?.decoderType ?? '').trim()

  // No purpose on the row. Input and SDI-12 decoders never have one, and an
  // output decoder the user cleared means it: null is the right write.
  if (!name) return { id: null }

  const wanted = normalise(name)
  const match = (types ?? []).find((type) => normalise(type.name) === wanted)
  const matchedId = positiveId(match?.id)
  if (matchedId) return { id: matchedId }

  // The row was loaded with an id and the name has not been touched since:
  // that id is still the answer, whatever the type list happens to hold.
  //
  // Only when untouched. On a row whose Purpose the user did edit, that id
  // belongs to the purpose that was there *before* — writing it would store
  // one purpose while the screen shows another, which is a quieter version of
  // the same bug.
  const edited = row?.__changedFields
  const nameWasEdited =
    edited instanceof Set
      ? edited.has('decoderType')
      : Array.isArray(edited) && edited.includes('decoderType')

  if (!nameWasEdited) {
    const current = positiveId(row?.decoderTypeId)
    if (current) return { id: current }
  }

  return { unresolved: name }
}
